"use client";

import { useEffect, useRef } from "react";

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY as
  | string
  | undefined;
const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback?: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "flexible" | "compact" | "invisible";
          action?: string;
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;
function loadScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${TURNSTILE_SCRIPT_URL}"]`,
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("load-failed")), {
        once: true,
      });
      return;
    }
    const s = document.createElement("script");
    s.src = TURNSTILE_SCRIPT_URL;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("load-failed"));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

interface Props {
  onToken: (token: string | null) => void;
  action?: string;
  theme?: "light" | "dark" | "auto";
}

/**
 * Invisible-aware Turnstile widget.
 * Renders the checkbox UI (user-visible). Token delivered via `onToken`.
 *
 * Safe to render conditionally - widget rebuilds when mounted.
 */
export function TurnstileWidget({
  onToken,
  action = "contact-form",
  theme = "auto",
}: Props) {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    if (!rootRef.current) return;
    if (widgetIdRef.current) return;

    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || !window.turnstile || !rootRef.current) return;
        widgetIdRef.current = window.turnstile.render(rootRef.current, {
          sitekey: TURNSTILE_SITE_KEY!,
          action,
          theme,
          size: "flexible",
          callback: (t) => onToken(t),
          "error-callback": () => onToken(null),
          "expired-callback": () => onToken(null),
        });
      })
      .catch(() => onToken(null));

    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          // noop
        }
        widgetIdRef.current = null;
      }
    };
    // onToken intentionally excluded - caller must memoize if it recreates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, theme]);

  if (!TURNSTILE_SITE_KEY) return null;

  return (
    <div
      ref={rootRef}
      className="mt-2 min-h-[65px]"
      aria-label="Verifica anti-bot Cloudflare Turnstile"
    />
  );
}

export function isTurnstileEnabled(): boolean {
  return Boolean(TURNSTILE_SITE_KEY);
}
